/* ==========================================================================
   Zero Fitness — side cart
   Owns every route into the cart: the header icon, any add-to-cart form on the
   page, quantity changes and the in-cart add-ons. State is never patched by
   hand; the section is re-rendered through the Section Rendering API so the
   markup always matches the Liquid.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ID = 'zero-cart';
  var root = document.documentElement;
  var lastFocus = null;
  var busy = false;
  var timerId = null;
  var TIMER_KEY = 'zfCartTimerStart';

  function el() {
    return document.querySelector('[data-zf-cart]');
  }

  function wrapper() {
    return document.getElementById('shopify-section-' + SECTION_ID);
  }

  /* --- Open / close ------------------------------------------------------ */

  function isOpen() {
    var node = el();
    return !!node && node.classList.contains('is-open');
  }

  function open(trigger) {
    var node = el();
    if (!node) return;
    lastFocus = trigger || document.activeElement;
    node.classList.add('is-open');
    root.classList.add('zf-cart-locked');
    var panel = node.querySelector('[data-zf-cart-panel]');
    if (panel) {
      // Every open starts with the machine and included extras. The panel now
      // owns scrolling; keep third-party footer insertion from anchoring it.
      panel.scrollTop = 0;
      var close = panel.querySelector('[data-zf-cart-close]');
      window.setTimeout(function () {
        (close || panel).focus({ preventScroll: true });
      }, 60);
    }
  }

  function close() {
    var node = el();
    if (!node) return;
    node.classList.remove('is-open');
    root.classList.remove('zf-cart-locked');
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus({ preventScroll: true });
    }
    lastFocus = null;
  }

  /* --- Rendering --------------------------------------------------------- */

  function sectionUrl() {
    var base = (window.routes && window.routes.root_url) || '/';
    if (base.slice(-1) !== '/') base += '/';
    return base + '?sections=' + SECTION_ID;
  }

  function refresh() {
    return fetch(sectionUrl(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[SECTION_ID];
        var host = wrapper();
        if (!html || !host) return;

        var reopen = isOpen();
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-zf-cart]');
        var current = host.querySelector('[data-zf-cart]');
        if (!fresh || !current) return;

        current.replaceWith(fresh);
        if (reopen) {
          fresh.classList.add('is-open');
          root.classList.add('zf-cart-locked');
        }
        syncBubble(fresh.getAttribute('data-item-count'));
        initTimer();
      })
      .catch(function () { /* leave the cart as it is rather than blanking it */ });
  }

  /* Keep the header count in step without re-rendering the whole header. */
  function syncBubble(count) {
    var n = parseInt(count, 10);
    if (isNaN(n)) return;
    var bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (bubble) bubble.textContent = n;
    var wrap = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (wrap) wrap.style.display = n > 0 ? '' : 'none';
  }

  /* --- Urgency timer -----------------------------------------------------
     Counts down how long the cart has been held in this session. Deliberately
     framed as the cart being saved, not stock being reserved, because Shopify
     does not hold inventory and promising otherwise would be a false claim.
     The start time persists per session so the clock does not reset on every
     re-render. --------------------------------------------------------------*/

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function initTimer() {
    if (timerId) {
      window.clearInterval(timerId);
      timerId = null;
    }

    var node = document.querySelector('[data-zf-timer]');
    var host = el();
    if (!node || !host) return;

    /* Empty cart clears the clock so the next basket starts fresh. */
    if (host.getAttribute('data-item-count') === '0') {
      try { window.sessionStorage.removeItem(TIMER_KEY); } catch (e) {}
      return;
    }

    var minutes = parseInt(node.getAttribute('data-zf-timer'), 10) || 15;
    var output = node.querySelector('[data-zf-timer-value]');
    if (!output) return;

    var start;
    try {
      start = parseInt(window.sessionStorage.getItem(TIMER_KEY), 10);
      if (!start || isNaN(start)) {
        start = Date.now();
        window.sessionStorage.setItem(TIMER_KEY, String(start));
      }
    } catch (e) {
      start = Date.now();
    }

    node.hidden = false;

    var tick = function () {
      var left = Math.round((start + minutes * 60000 - Date.now()) / 1000);
      if (left <= 0) {
        output.textContent = '00:00';
        node.classList.add('is-expired');
        window.clearInterval(timerId);
        timerId = null;
        return;
      }
      output.textContent = pad(Math.floor(left / 60)) + ':' + pad(left % 60);
    };

    tick();
    timerId = window.setInterval(tick, 1000);
  }

  /* --- Cart operations --------------------------------------------------- */

  /* Shopify returns 422 with a readable reason (out of stock, quantity limits).
     Swallowing it leaves the customer clicking a button that does nothing, so
     the message is surfaced in the drawer. */
  function send(url, payload, asForm) {
    var init = { method: 'POST', headers: { Accept: 'application/json' } };
    if (asForm) {
      init.body = payload;
    } else {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(payload);
    }
    return fetch(url, init).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'That could not be added to your cart.');
          error.isCartError = true;
          throw error;
        }
        return data;
      });
    });
  }

  function showError(message) {
    var node = el();
    if (!node || !message) return;
    var panel = node.querySelector('[data-zf-cart-panel]');
    if (!panel) return;
    var existing = panel.querySelector('[data-zf-cart-error]');
    if (existing) existing.remove();
    var box = document.createElement('p');
    box.className = 'zf-cart__error';
    box.setAttribute('data-zf-cart-error', '');
    box.setAttribute('role', 'alert');
    box.textContent = message;
    var head = panel.querySelector('.zf-cart__head');
    if (head && head.nextSibling) panel.insertBefore(box, head.nextSibling);
    else panel.appendChild(box);
  }

  function guard(fn) {
    if (busy) return Promise.resolve();
    busy = true;
    var node = el();
    if (node) node.classList.add('is-busy');

    var failure = null;
    return fn()
      .catch(function (error) { failure = error; })
      .then(function () { return refresh(); })
      .then(function () {
        busy = false;
        var after = el();
        if (after) after.classList.remove('is-busy');
        if (failure) {
          showError(failure.message);
          throw failure;
        }
      });
  }

  function cartUrl(key, fallback) {
    return (window.routes && window.routes[key] ? window.routes[key] : fallback) + '.js';
  }

  function changeLine(line, quantity) {
    return guard(function () {
      return send(cartUrl('cart_change_url', '/cart/change'), { line: line, quantity: quantity });
    }).catch(function () { /* already surfaced */ });
  }

  function addVariant(id, quantity) {
    return guard(function () {
      return send(cartUrl('cart_add_url', '/cart/add'), { items: [{ id: id, quantity: quantity || 1 }] });
    }).then(function () {
      open();
    }).catch(function () {
      open();
    });
  }

  /* --- Wiring ------------------------------------------------------------ */

  /* The header icon is a plain link to /cart that the stock drawer script also
     binds to. Capture phase plus stopImmediatePropagation makes this the only
     handler that runs, whichever loaded first. */
  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest('#cart-icon-bubble, [data-zf-cart-open]');
      if (trigger && el()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        open(trigger);
      }
    },
    true
  );

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-zf-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    var qty = event.target.closest('[data-zf-qty-change]');
    if (qty) {
      event.preventDefault();
      var item = qty.closest('[data-zf-line]');
      if (!item) return;
      changeLine(parseInt(item.getAttribute('data-zf-line'), 10), parseInt(qty.getAttribute('data-zf-qty-change'), 10));
      return;
    }

    var add = event.target.closest('[data-zf-cart-add]');
    if (add) {
      event.preventDefault();
      addVariant(add.getAttribute('data-zf-cart-add'), 1);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen()) close();
  });

  document.addEventListener('change', function (event) {
    var toggle = event.target;
    if (!(toggle instanceof HTMLInputElement) || !toggle.matches('[data-zf-installation-toggle]')) return;
    if (toggle.form) toggle.form.classList.toggle('zf-installation-selected', toggle.checked);
  });

  /* Any add-to-cart form on the site opens the drawer instead of reloading. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      if (!el()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var data = new FormData(form);
      var installation = form.querySelector('[data-zf-installation-toggle]:checked');
      var submit = form.querySelector('[type="submit"]');
      if (submit) submit.setAttribute('aria-busy', 'true');

      var done = function () {
        if (submit) submit.removeAttribute('aria-busy');
        open(submit);
      };

      guard(function () {
        if (installation) {
          var properties = {};
          var productHeading = document.querySelector('h1');
          data.forEach(function (value, key) {
            var match = key.match(/^properties\[(.+)\]$/);
            if (match && value) properties[match[1]] = value;
          });

          return send(cartUrl('cart_add_url', '/cart/add'), {
            items: [
              {
                id: Number(data.get('id')),
                quantity: Number(data.get('quantity')) || 1,
                properties: properties
              },
              {
                id: Number(installation.value),
                quantity: 1,
                properties: { 'Added with': productHeading ? productHeading.textContent.trim() : 'Machine' }
              }
            ]
          });
        }

        return send(cartUrl('cart_add_url', '/cart/add'), data, true);
      }).then(done, done);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTimer);
  } else {
    initTimer();
  }

  /* Re-open after the theme editor re-renders this section. */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.detail && event.detail.sectionId === SECTION_ID) syncBubble(el() && el().getAttribute('data-item-count'));
  });
})();
